import React from 'react';
import ProfilePicture from './ProfilePicture';
import './ManagerCard.css';

interface Manager {
  userno: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  full_name: string;
}

interface ManagerMetrics {
  total_loans: number;
  current_loans: number;
  past_due_loans: number;
  one_to_10: number;
  eleven_to_30: number;
  thirty_one_to_60: number;
  sixty_one_to_90: number;
  ninety_one_to_120: number;
  one_twenty_one_plus: number;
  thirty_plus: number;
  total_balance: number;
  pct_current: number;
  pct_past_due: number;
  pct_one_to_10: number;
  pct_eleven_to_30: number;
  pct_thirty_one_to_60: number;
  pct_sixty_one_to_90: number;
  pct_ninety_one_to_120: number;
  pct_one_twenty_one_plus: number;
  pct_thirty_plus: number;
}

interface ManagerCardProps {
  manager: Manager;
  metrics: ManagerMetrics;
  onManagerClick: (manager: Manager) => void;
  onViewAllAccounts: (manager: Manager) => void;
  loading?: boolean;
}

const ManagerCard: React.FC<ManagerCardProps> = ({
  manager,
  metrics,
  onManagerClick,
  onViewAllAccounts,
  loading = false
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Note: ProfilePicture component will handle photo loading and fallbacks

  const getHealthColor = (pctPastDue: number): string => {
    if (pctPastDue < 5) return '#10b981'; // green
    if (pctPastDue < 15) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div 
      className="manager-card"
      onClick={() => onManagerClick(manager)}
    >
      <div className="manager-header">
        <div className="manager-photo">
          <ProfilePicture 
            size={80}
            showBorder={true}
            borderColor="#e5e7eb"
            userEmail={manager.email}
          />
        </div>
        <div className="manager-info">
          <h3 className="manager-name">{manager.full_name}</h3>
          <p className="manager-username">@{manager.username}</p>
          <p className="manager-email">{manager.email}</p>
        </div>
        <div className="health-indicator">
          <div 
            className="health-dot"
            style={{ backgroundColor: getHealthColor(metrics.pct_past_due) }}
          ></div>
        </div>
      </div>

      <div className="manager-metrics">
        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Total Loans</span>
            <span className="metric-value">{metrics.total_loans.toLocaleString()}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Total Balance</span>
            <span className="metric-value">{formatCurrency(metrics.total_balance)}</span>
          </div>
        </div>

        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Current</span>
            <span className="metric-value current">{formatPercent(metrics.pct_current)}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Past Due</span>
            <span className="metric-value past-due">{formatPercent(metrics.pct_past_due)}</span>
          </div>
        </div>

        <div className="past-due-breakdown">
          <div className="breakdown-item">
            <span className="breakdown-label">1-10 days</span>
            <span className="breakdown-value">{metrics.one_to_10}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">11-30 days</span>
            <span className="breakdown-value">{metrics.eleven_to_30}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">31-60 days</span>
            <span className="breakdown-value">{metrics.thirty_one_to_60}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">61-90 days</span>
            <span className="breakdown-value">{metrics.sixty_one_to_90}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">91-120 days</span>
            <span className="breakdown-value">{metrics.ninety_one_to_120}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">120+ days</span>
            <span className="breakdown-value">{metrics.one_twenty_one_plus}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">30+ days</span>
            <span className="breakdown-value">{metrics.thirty_plus}</span>
          </div>
        </div>
      </div>

      <div className="manager-actions">
        <button 
          className="view-all-accounts-btn"
          onClick={(e) => {
            e.stopPropagation();
            onViewAllAccounts(manager);
          }}
        >
          View All Accounts
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="click-hint">
        <span>Click to view collection officers</span>
      </div>
    </div>
  );
};

export default ManagerCard;
